import { useBloc as useBlocNext } from "@blac/react";
import { Blac } from "blac-next";
import clsx from "clsx";
import type { FC } from "react";
import React, { useEffect } from "react";
import { logAverageTimes } from "src/api/ApiController";
import Language, { Locale } from "src/constants/language";
import { BiometricVerificationBloc } from "src/hybrid/components/BiometricVerification";
import envVariables from "src/lib/envVariables";
import { changeLanguage, getSupportedUserLocale } from "src/lib/i18next";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { DeviceTrackingBloc } from "src/state/DeviceTrackingBloc/DeviceTrackingBloc";
import HealthSyncBloc from "src/state/HealthSyncBloc/HealthSyncBloc";
import AuthenticationBloc from "src/state/UserCubit/AuthenticationBloc";
import UserCubit from "src/state/UserCubit/UserCubit";
import UserPreferencesCubit, {
  UserPreferenceKeys
} from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import { toast, useBloc, userState } from "src/state/state";
import {
  AppPopup,
  AppQueryPopupsController
} from "src/ui/components/AppQueryPopups/AppQueryPopupsBloc";
import AppDebugCubit from "./AppDebugCubit";
import {
  DebugBadge,
  DebugButton,
  DebugButtonRow,
  DebugCategoryHeader,
  DebugContainer,
  DebugDivider,
  DebugFormGroup,
  DebugKeyValue,
  DebugToggle
} from "./debug.styles";
import DebugActiveProgram from "./DebugActiveProgram";
import DebugAppointmentScheduler from "./DebugAppointmentScheduler";
import DebugCapInfo from "./DebugCapInfo";
import DebugExperienceGroup from "./DebugExperienceGroup";
import DebugFeatureFlags from "./DebugFeatureFlags";
import DebugLoadingOverlay from "./DebugLoadingOverlay";
import DebugPartnerSession from "./DebugPartnerSession";
import DebugSection from "./DebugSection";
import DebugTaskMocker from "./DebugTaskMocker";
import DebugUserJourney from "./DebugUserJourney";
import DebugUserStorage from "./DebugUserStorage";
import DebugVersion from "./DebugVersion";

const appDebugCubit = Blac.getBloc(AppDebugCubit);

const EditPrefValue: FC<{
  value: string;
  propKey: string;
  onChange: (k: string, v: unknown) => void;
}> = ({ value, propKey, onChange }) => {
  const [editing, setEditing] = React.useState(false);
  const [newValue, setNewValue] = React.useState(value);

  const save = () => {
    if (newValue === "") {
      return;
    }

    try {
      const parsed = JSON.parse(newValue);
      onChange(propKey, parsed);
      setEditing(false);
    } catch {
      toast.error("debug_invalid_json" as never);
    }
  };

  return (
    <DebugKeyValue>
      <span className="key">{propKey}</span>
      {editing ? (
        <div style={{ flex: 1, display: "flex", gap: "0.5rem" }}>
          <input
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            style={{ flex: 1 }}
          />
          <DebugButton className="small" onClick={save}>
            Save
          </DebugButton>
          <DebugButton className="small" onClick={() => setEditing(false)}>
            Cancel
          </DebugButton>
        </div>
      ) : (
        <div
          style={{
            flex: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "0.5rem"
          }}
        >
          <span className="value mono" style={{ flex: 1 }}>
            {value}
          </span>
          <DebugButton className="small" onClick={() => setEditing(true)}>
            Edit
          </DebugButton>
        </div>
      )}
    </DebugKeyValue>
  );
};

const DebugUserDetails: FC = () => {
  const [{ userData }] = useBloc(UserCubit);

  if (!userData) {
    return <p style={{ color: "#666" }}>No user data available</p>;
  }

  return (
    <div>
      <DebugKeyValue>
        <span className="key">Email</span>
        <span className="value">{userData.email || "—"}</span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">User ID</span>
        <span className="value mono" style={{ fontSize: "11px" }}>
          {userData.id || "—"}
        </span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Email Verified</span>
        <span className="value">
          {userData.emailVerified ? (
            <DebugBadge variant="success">Yes</DebugBadge>
          ) : (
            <DebugBadge variant="danger">No</DebugBadge>
          )}
        </span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Auth Method</span>
        <span className="value">
          {userData.sessionAuthenticationMethod || "—"}
        </span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Password Set</span>
        <span className="value">
          {userData.passwordSet ? (
            <DebugBadge variant="success">Yes</DebugBadge>
          ) : (
            <DebugBadge variant="warning">No</DebugBadge>
          )}
        </span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">MFA Active</span>
        <span className="value">
          {userData.mfaActive ? (
            <DebugBadge variant="success">Yes</DebugBadge>
          ) : (
            <DebugBadge variant="info">No</DebugBadge>
          )}
        </span>
      </DebugKeyValue>
      {userData.analyticsToken && (
        <DebugKeyValue>
          <span className="key">Analytics</span>
          <span
            className="value mono"
            style={{ fontSize: "10px", wordBreak: "break-all" }}
          >
            {userData.analyticsToken}
          </span>
        </DebugKeyValue>
      )}
    </div>
  );
};

const DebugAuth: FC = () => {
  const [, auth] = useBlocNext(AuthenticationBloc);
  const [bioState, bio] = useBloc(BiometricVerificationBloc);
  const [server, setServer] = React.useState<string | null>();

  React.useEffect(() => {
    void bio.getServer().then((server) => setServer(server));
  }, []);

  return (
    <div>
      <DebugButtonRow>
        <DebugButton className="danger" onClick={() => void auth.logout()}>
          Logout
        </DebugButton>
        <DebugButton onClick={() => void auth.refreshAccessToken()}>
          Refresh Token
        </DebugButton>
      </DebugButtonRow>

      <DebugDivider />

      <h4 style={{ fontSize: "13px", margin: "0 0 0.75rem", fontWeight: 600 }}>
        Biometric Lock
      </h4>
      <DebugKeyValue>
        <span className="key">Enabled</span>
        <span className="value">
          {bioState.isBiometricVerificationEnabled ? (
            <DebugBadge variant="success">Yes</DebugBadge>
          ) : (
            <DebugBadge variant="info">No</DebugBadge>
          )}
        </span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Available</span>
        <span className="value">
          {bioState.isBiometricVerificationAvailable ? (
            <DebugBadge variant="success">Yes</DebugBadge>
          ) : (
            <DebugBadge variant="info">No</DebugBadge>
          )}
        </span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Type</span>
        <span className="value">{bioState.biometricType || "—"}</span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Server</span>
        <span className="value mono">{server || "—"}</span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Session Verified</span>
        <span className="value">
          {bioState.isSessionVerified ? (
            <DebugBadge variant="success">Yes</DebugBadge>
          ) : (
            <DebugBadge variant="info">No</DebugBadge>
          )}
        </span>
      </DebugKeyValue>
    </div>
  );
};

const DebugHealthSync: FC = () => {
  const [healthState, { runBgTask }] = useBloc(HealthSyncBloc);

  return (
    <div>
      <DebugKeyValue>
        <span className="key">Available</span>
        <span className="value">
          {healthState.available ? (
            <DebugBadge variant="success">Yes</DebugBadge>
          ) : (
            <DebugBadge variant="info">No</DebugBadge>
          )}
        </span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Steps Auth</span>
        <span className="value">{healthState.stepsAuthorized || "—"}</span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Steps Data</span>
        <span className="value">{healthState.stepsData.length} records</span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Last Sync</span>
        <span className="value">
          {healthState.lastImported?.toLocaleString() || "Never"}
        </span>
      </DebugKeyValue>

      <DebugButtonRow style={{ marginTop: "0.75rem" }}>
        <DebugButton
          onClick={() =>
            AppQueryPopupsController.openPopup(AppPopup.healthSyncSetup)
          }
        >
          Open Setup
        </DebugButton>
        <DebugButton onClick={() => void runBgTask()}>
          Run Background Task
        </DebugButton>
      </DebugButtonRow>
    </div>
  );
};

const DebugDeviceTracking: FC = () => {
  const [deviceState, deviceTracking] = useBlocNext(DeviceTrackingBloc);

  return (
    <div>
      <DebugKeyValue>
        <span className="key">Collected</span>
        <span className="value">
          {deviceState.collected ? (
            <DebugBadge variant="success">Yes</DebugBadge>
          ) : (
            <DebugBadge variant="info">No</DebugBadge>
          )}
        </span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Collection Time</span>
        <span className="value">
          {deviceState.collectionTime ? `${deviceState.collectionTime}ms` : "—"}
        </span>
      </DebugKeyValue>
      {deviceState.error && (
        <DebugKeyValue>
          <span className="key">Error</span>
          <span className="value danger">{deviceState.error.message}</span>
        </DebugKeyValue>
      )}

      <DebugButtonRow style={{ marginTop: "0.75rem" }}>
        <DebugButton onClick={() => void deviceTracking.collectAndTrack()}>
          Collect Device Info
        </DebugButton>
      </DebugButtonRow>

      {deviceState.deviceInfo && (
        <details style={{ marginTop: "0.75rem" }}>
          <summary
            style={{ cursor: "pointer", fontSize: "12px", color: "#666" }}
          >
            View Device Info JSON
          </summary>
          <pre
            style={{
              fontSize: "10px",
              background: "#f5f5f7",
              padding: "0.5rem",
              borderRadius: "4px",
              overflow: "auto",
              marginTop: "0.5rem"
            }}
          >
            {JSON.stringify(deviceState.deviceInfo, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
};

const EnvGroup: FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children
}) => (
  <div style={{ marginBottom: "1rem" }}>
    <h5
      style={{
        fontSize: "11px",
        fontWeight: 600,
        color: "#666",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        margin: "0 0 0.5rem",
        paddingBottom: "0.25rem",
        borderBottom: "1px solid #e0e0e0"
      }}
    >
      {title}
    </h5>
    {children}
  </div>
);

const EnvBooleanRow: FC<{ label: string; value: boolean }> = ({
  label,
  value
}) => (
  <DebugKeyValue>
    <span className="key">{label}</span>
    <span className="value">
      <DebugBadge variant={value ? "success" : "info"}>
        {value ? "true" : "false"}
      </DebugBadge>
    </span>
  </DebugKeyValue>
);

const EnvTextRow: FC<{ label: string; value: string; isUrl?: boolean }> = ({
  label,
  value,
  isUrl
}) => (
  <DebugKeyValue>
    <span className="key">{label}</span>
    <span
      className="value mono"
      style={{
        fontSize: "11px",
        wordBreak: isUrl ? "break-all" : "break-word"
      }}
    >
      {value || "—"}
    </span>
  </DebugKeyValue>
);

const DebugEnvVars: FC = () => {
  return (
    <div>
      <EnvGroup title="Environment">
        <EnvBooleanRow label="PROD" value={envVariables.PROD} />
        <EnvBooleanRow label="DEV" value={envVariables.DEV} />
        <EnvBooleanRow label="DEBUG" value={envVariables.DEBUG} />
        <EnvBooleanRow label="TRACKING" value={envVariables.TRACKING} />
        <EnvTextRow label="ENV_NAME" value={envVariables.ENV_NAME} />
        <EnvTextRow label="APP_ENV" value={envVariables.APP_ENV} />
        <EnvTextRow label="BUILD_VERSION" value={envVariables.BUILD_VERSION} />
      </EnvGroup>

      <EnvGroup title="API Endpoints">
        <EnvTextRow
          label="API_BASE_URL"
          value={envVariables.API_BASE_URL}
          isUrl
        />
        <EnvTextRow
          label="API_BASE_URL_LEGACY"
          value={envVariables.API_BASE_URL_LEGACY}
          isUrl
        />
        <EnvTextRow
          label="WS_BASE_URL"
          value={envVariables.WS_BASE_URL}
          isUrl
        />
        <EnvTextRow label="WEB_URL" value={envVariables.WEB_URL} isUrl />
        <EnvTextRow
          label="CMS_API_BASE_URL"
          value={envVariables.CMS_API_BASE_URL}
          isUrl
        />
        <EnvTextRow
          label="RXDIET_IFRAME_URL"
          value={envVariables.RXDIET_IFRAME_URL}
          isUrl
        />
      </EnvGroup>

      <EnvGroup title="Analytics">
        <EnvTextRow
          label="MIXPANEL_TOKEN"
          value={envVariables.MIXPANEL_TOKEN}
        />
        <EnvTextRow label="SENTRY_DSN" value={envVariables.SENTRY_DSN} isUrl />
        <EnvTextRow
          label="CUSTOMER_IO_SITE_ID"
          value={envVariables.CUSTOMER_IO_SITE_ID}
        />
      </EnvGroup>

      <EnvGroup title="Integrations">
        <EnvTextRow
          label="STRIPE_PUBLISHABLE_KEY"
          value={envVariables.STRIPE_PUBLISHABLE_KEY}
        />
        <EnvTextRow
          label="STRIPE_ACCOUNT_ID"
          value={envVariables.STRIPE_ACCOUNT_ID}
        />
        <EnvTextRow
          label="SANITY_PROJECT_ID"
          value={envVariables.SANITY_PROJECT_ID}
        />
        <EnvTextRow
          label="SANITY_DATASET"
          value={envVariables.SANITY_STUDIO_API_DATASET}
        />
        <EnvTextRow
          label="APPLICATION_ID"
          value={envVariables.APPLICATION_ID}
        />
        <EnvTextRow label="VIMEO_TOKEN" value={envVariables.VIMEO_TOKEN} />
      </EnvGroup>

      <EnvGroup title="Partner SSO">
        <EnvTextRow
          label="SSO_LINK_TRANSCARENT"
          value={envVariables.SSO_LINK_TRANSCARENT}
          isUrl
        />
        <EnvTextRow
          label="TRANSCARENT_DYNAMIC_APP_URL"
          value={envVariables.TRANSCARENT_DYNAMIC_APP_URL}
          isUrl
        />
      </EnvGroup>

      <EnvGroup title="Browser">
        <EnvTextRow label="href" value={window.location.href} isUrl />
        <EnvTextRow label="hostname" value={window.location.hostname} />
      </EnvGroup>
    </div>
  );
};

const AppDebug: FC = () => {
  const [{ show, extra }, cubit] = useBlocNext(AppDebugCubit);
  const [userPref, { updateUserPreferences }] = useBloc(UserPreferencesCubit);
  const [toggle, setToggle] = React.useState(
    sessionStorage.getItem(appDebugCubit.isMinifiedSessStorageKey) === "true"
  );

  useEffect(() => {
    sessionStorage.setItem(
      appDebugCubit.isMinifiedSessStorageKey,
      String(toggle)
    );
  }, [toggle]);

  const locale = getSupportedUserLocale();

  useEffect(() => {
    cubit.addCustomListeners();
    cubit.init();
    return cubit.removeCustomListeners;
  }, []);

  const handlePrefChange = (k: string, v: unknown) => {
    void updateUserPreferences({ [k]: v }).then(() => {
      toast.success("debug_preference_saved" as never);
    });
  };

  if (!show) return <></>;

  const populate = () => {
    void userState.populateUserprofileWithDemoData();
  };

  const handleChange = (loc: Locale) => {
    void updateUserPreferences({
      [UserPreferenceKeys.language]: loc
    });
    void changeLanguage(loc);
  };

  return (
    <DebugContainer aria-hidden="true" className={clsx({ toggle })}>
      <DebugToggle onClick={() => setToggle((s) => !s)} />

      <DebugButtonRow>
        <DebugButton onClick={() => cubit.setShow(false)}>
          Close Debug
        </DebugButton>
        <DebugButton onClick={() => window.location.reload()}>
          Reload
        </DebugButton>
      </DebugButtonRow>

      <DebugCategoryHeader>Account</DebugCategoryHeader>
      <DebugSection title="Profile">
        <DebugUserDetails />
      </DebugSection>
      <DebugSection title="Preferences">
        {Object.keys(userPref).map((k) => {
          const val = JSON.stringify(
            (userPref as unknown as Record<string, unknown>)[k]
          );
          return (
            <EditPrefValue
              key={k}
              value={val}
              propKey={k}
              onChange={handlePrefChange}
            />
          );
        })}
      </DebugSection>
      <DebugSection title="Authentication">
        <DebugAuth />
      </DebugSection>
      <DebugSection title="Local Storage">
        <DebugUserStorage />
      </DebugSection>

      <DebugCategoryHeader>Configuration</DebugCategoryHeader>
      <DebugSection title="Feature Flags">
        <DebugFeatureFlags />
      </DebugSection>
      <DebugSection title="Experience Group">
        <DebugExperienceGroup />
      </DebugSection>
      <DebugSection title="Program Membership">
        <DebugActiveProgram />
      </DebugSection>
      <DebugSection title="Journey Progress">
        <DebugUserJourney />
      </DebugSection>
      <DebugSection title="Partner Mode">
        <DebugPartnerSession />
      </DebugSection>

      <DebugCategoryHeader>Simulations</DebugCategoryHeader>
      <DebugSection title="Task Simulator">
        <DebugTaskMocker />
      </DebugSection>
      <DebugSection title="Loading States">
        <DebugLoadingOverlay />
      </DebugSection>
      <DebugSection title="Appointments">
        <DebugAppointmentScheduler />
      </DebugSection>
      <DebugSection title="Session Dialog">
        <DebugButton
          onClick={() =>
            AppQueryPopupsController.openPopup(AppPopup.sessionConfirmation, {
              additionalParameters: { stay: "false" }
            })
          }
        >
          Open Session Dialog
        </DebugButton>
      </DebugSection>

      <DebugCategoryHeader>Device & Health</DebugCategoryHeader>
      <DebugSection title="HealthKit / Health Connect">
        <DebugHealthSync />
      </DebugSection>
      <DebugSection title="Device Fingerprint">
        <DebugDeviceTracking />
      </DebugSection>
      <DebugSection title="Native App">
        <DebugCapInfo />
      </DebugSection>
      <DebugSection title="OTA Bundles">
        <DebugVersion />
      </DebugSection>

      <DebugCategoryHeader>Environment</DebugCategoryHeader>
      <DebugSection title="Variables">
        <DebugEnvVars />
      </DebugSection>
      {Object.keys(extra).length > 0 && (
        <DebugSection title="Custom Data">
          {Object.keys(extra).map((k) => (
            <DebugKeyValue key={k}>
              <span className="key">{k}</span>
              <span className="value">{String(extra[k])}</span>
            </DebugKeyValue>
          ))}
        </DebugSection>
      )}

      <DebugDivider />

      <DebugFormGroup>
        <label>Language</label>
        <select
          name="language"
          value={locale}
          onChange={(e) => handleChange(e.target.value as Locale)}
          style={{ width: "auto" }}
        >
          <option value={Locale.en} key={Language.en}>
            English
          </option>
          <option value={Locale.es} key={Language.es}>
            Español
          </option>
        </select>
      </DebugFormGroup>

      <DebugDivider />

      <DebugButtonRow>
        <DebugButton onClick={() => logAverageTimes()}>
          Log API Timings
        </DebugButton>
        <DebugButton
          className="danger"
          onClick={() => reportErrorSentry(new Error("Debug Error"))}
        >
          Test Sentry
        </DebugButton>
        <DebugButton onClick={() => populate()}>Fill Demo Data</DebugButton>
      </DebugButtonRow>

      <div style={{ height: "2rem" }} />
    </DebugContainer>
  );
};

export default AppDebug;

export { appDebugCubit as DebugBlocController };
